import { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  Alert, IconButton, Tooltip, TextField, MenuItem, Stack, Tabs, Tab
} from '@mui/material';
import { InsertDriveFile as FileIcon, EventNote as CalendarIcon } from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateFormatter';

import { openScriptSecure } from '../utils/fileUrl';

const STATUS_COLORS = {
  'Content Ready': 'info',
  'Content Approved': 'warning',
  'Planning Completed': 'default',
  'Shoot Completed': 'warning',
  'Editing Completed': 'secondary',
  'Released': 'success',
  'Sent for Revision': 'error'
};

const MY_ROLE_LABEL = {
  director: 'Director',
  editor: 'Editor',
  cinematographer: 'Cinematographer',
  writer: 'Writer',
  cast: 'Cast'
};

const ROLE_CHIP_COLOR = {
  director: 'primary',
  editor: 'secondary',
  cinematographer: 'default',
  writer: 'default',
  cast: 'warning'
};

/**
 * Tab classification matrix per role:
 *
 * Status              Director  Editor  Cinematographer  Cast/Actor  Writer
 * Content Ready       Current   —       Current          Current     Current
 * Content Approved    Current   —       Current          Current     Current
 * Planning Completed  Current   —       Current          Current     Current
 * Sent for Revision   Current   —       Current          Current     Current
 * Shoot Completed     Current   Current Completed        Completed   Current
 * Editing Completed   Completed Completed Completed      Completed   Completed
 * Released            Completed Completed Completed      Completed   Completed
 */
const CURRENT_STATUSES = {
  director:        ['Content Ready', 'Content Approved', 'Planning Completed', 'Sent for Revision', 'Shoot Completed'],
  editor:          ['Shoot Completed'],
  cinematographer: ['Content Ready', 'Content Approved', 'Planning Completed', 'Sent for Revision'],
  writer:          ['Content Ready', 'Content Approved', 'Planning Completed', 'Sent for Revision', 'Shoot Completed'],
  cast:            ['Content Ready', 'Content Approved', 'Planning Completed', 'Sent for Revision'],
};

const COMPLETED_STATUSES = ['Editing Completed', 'Released'];

function parseScriptFile(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch (_) { return null; } }
  return raw;
}

function ContentTable({ items, getMyRole }) {
  if (items.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Typography color="text.secondary">No content in this tab.</Typography>
      </Box>
    );
  }
  return (
    <>
      {/* Desktop */}
      <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Channel</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Director</strong></TableCell>
              <TableCell><strong>My Role</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Shoot Date</strong></TableCell>
              <TableCell align="center"><strong>Script</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => {
              const myRole = getMyRole(item);
              const sf = parseScriptFile(item.scriptFile);
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.contentTitle}</Typography>
                    <Typography variant="caption" color="primary">{item.contentId}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.channel} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.contentType}</TableCell>
                  <TableCell>{item.director || '—'}</TableCell>
                  <TableCell>
                    {myRole && (
                      <Chip
                        label={MY_ROLE_LABEL[myRole] || myRole}
                        size="small"
                        color={ROLE_CHIP_COLOR[myRole] || 'default'}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.status} size="small" color={STATUS_COLORS[item.status] || 'default'} />
                  </TableCell>
                  <TableCell>{formatDate(item.shootDate) || '—'}</TableCell>
                  <TableCell align="center">
                    {sf ? (
                      <Tooltip title={`Download: ${sf.originalName}`} arrow>
                        <IconButton
                          size="small" color="primary"
                          onClick={() => openScriptSecure(item.id)}
                        >
                          <FileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile cards */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1 }}>
        <Stack spacing={2}>
          {items.map(item => {
            const myRole = getMyRole(item);
            const sf = parseScriptFile(item.scriptFile);
            return (
              <Card key={item.id} variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box flex={1}>
                      <Typography variant="caption" color="primary" fontWeight={700}>{item.contentId}</Typography>
                      <Typography variant="h6" fontWeight={600}>{item.contentTitle}</Typography>
                    </Box>
                    {sf && (
                      <Tooltip title={sf.originalName} arrow>
                        <IconButton size="small" color="primary"
                          onClick={() => openScriptSecure(item.id)}>
                          <FileIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={item.channel} size="small" variant="outlined" />
                    <Chip label={item.contentType} size="small" variant="outlined" />
                    {myRole && (
                      <Chip label={MY_ROLE_LABEL[myRole]} size="small"
                        color={ROLE_CHIP_COLOR[myRole] || 'default'} variant="outlined" />
                    )}
                    <Chip label={item.status} size="small" color={STATUS_COLORS[item.status] || 'default'} />
                  </Box>
                  <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Director: {item.director || '—'} · Shoot: {formatDate(item.shootDate) || '—'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>
    </>
  );
}

function MySchedule() {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0); // 0 = Current, 1 = Completed
  const [search, setSearch] = useState('');
  const [dayRange, setDayRange] = useState(30); // 30 | 60 | 90 | 0 (all)

  useEffect(() => { fetchSchedule(); }, []);

  const fetchSchedule = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/content/my-schedule');
      setContent(res.data.data.content || []);
    } catch (_) {
      setError('Failed to load your schedule.');
    } finally {
      setLoading(false);
    }
  };

  const getMyRole = (item) => {
    const u = user?.username;
    if (!u) return '';
    if (item.directorId === u) return 'director';
    if (item.editorId === u) return 'editor';
    if (item.cinematographerId === u) return 'cinematographer';
    if (item.writerId === u) return 'writer';
    let ids = item.castingIds || [];
    if (typeof ids === 'string') { try { ids = JSON.parse(ids); } catch (_) { ids = []; } }
    if (Array.isArray(ids) && ids.includes(u)) return 'cast';
    return '';
  };

  const classify = (item) => {
    const role = getMyRole(item);
    if (!role) return null;
    if (COMPLETED_STATUSES.includes(item.status)) return 'completed';
    const currentList = CURRENT_STATUSES[role] || [];
    if (currentList.includes(item.status)) return 'current';
    return null; // hidden (e.g. editor with Content Ready)
  };

  const applySearch = (items) => {
    let result = items;
    if (search) result = result.filter(i => i.contentTitle?.toLowerCase().includes(search.toLowerCase()));
    if (dayRange > 0) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dayRange);
      cutoff.setHours(0, 0, 0, 0);
      result = result.filter(i => {
        if (!i.shootDate) return true; // no shoot date — always show
        return new Date(i.shootDate) >= cutoff;
      });
    }
    return result;
  };

  const currentItems  = applySearch(content.filter(i => classify(i) === 'current'));
  const completedItems = applySearch(content.filter(i => classify(i) === 'completed'));

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
              My Schedule / Work List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Content you are assigned to as Director, Editor, Cinematographer, Writer or Cast
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Search + Date range filter */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField
          size="small" label="Search title" value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select size="small" label="Shoot Date Range" value={dayRange}
          onChange={e => setDayRange(Number(e.target.value))}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value={30}>Last 30 days</MenuItem>
          <MenuItem value={60}>Last 60 days</MenuItem>
          <MenuItem value={90}>Last 90 days</MenuItem>
          <MenuItem value={0}>All time</MenuItem>
        </TextField>
      </Stack>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && (
        <Card>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Current Schedule
                  <Chip label={currentItems.length} size="small" color="primary" />
                </Box>
              }
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Completed
                  <Chip label={completedItems.length} size="small" color="success" />
                </Box>
              }
            />
          </Tabs>
          <CardContent sx={{ p: 0 }}>
            {tab === 0 && <ContentTable items={currentItems} getMyRole={getMyRole} />}
            {tab === 1 && <ContentTable items={completedItems} getMyRole={getMyRole} />}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default MySchedule;
